<template lang="pug">
    .error(v-if="!showPage")
        Error(:message="null" :code="403")
    section.communication-accounts(v-else)
        //- The one section that does NOT fold: it is what the page is for, and
        //- the WhatsApp template manager is opened from it.
        .section-intro
            h4 {{ $t('Connections') }}
            p.help {{ communicationEnabled ? $t('Email + WhatsApp / messenger channels. Click a connection to edit it.') : $t('Email sender configuration. Click a connection to edit it.') }}

        communication-connections(
            v-if="settingsForm"
            ref="connections"
            :cms-id="activeCms.id"
            :mailbox="mailboxModel"
            :newsletter-mailbox="newsletterModel"
            :show-newsletter="hasNewsletter"
            :communication-enabled="communicationEnabled"
            scope="cms"
            @persist-mailbox="persistCmsMailbox"
            @delete-mailbox="persistCmsMailbox"
            @persist-newsletter="persistCmsNewsletter"
            @whatsapp-changed="onWhatsAppChanged"
        )

        template(v-if="communicationEnabled")
            //- Signatures and Textvorlagen are the same idea — wording the team
            //- inserts instead of typing it again — and used to sit as two
            //- separate sections with near-identical one-column tables 40px apart.
            //- One section, two sub-blocks, in the auto-reply channel idiom below.
            .section-intro.reusable-text-intro.is-collapsible(@click="showReusableText = !showReusableText")
                h4
                    i.section-intro__chevron(:class="showReusableText ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
                    |  {{ $t('Reusable text') }}
                p.help {{ $t('Wording your team inserts instead of typing it again: a sign-off at the end of a message, a prepared reply as its body.') }}

            //- v-show, not v-if: both tables are measured through $refs when the
            //- section opens, and an unmounted ref cannot be laid out.
            //- Two peers: same kind of thing, comparable size, so they sit beside
            //- each other where the window allows and stack when it does not.
            .settings-columns(v-show="showReusableText")
                //- Presets, signatures and the suggestion mode are all part of the
                //- CMS, so they bind straight to the settings form and the page's
                //- own Save persists them — like every other setting on this tab.
                message-preset-manager(
                    ref="messagePresets"
                    nested
                    :cms-id="activeCms.id"
                    :mode="settingsForm.preset_suggestion_mode || 'off'"
                    :presets="settingsForm.messagePresets || []"
                    @update:mode="value => $set(settingsForm, 'preset_suggestion_mode', value)"
                    @update:presets="value => $set(settingsForm, 'messagePresets', value)"
                )

                .settings-panel
                    .settings-panel__head
                        span.settings-panel__name {{ $t('Email signatures') }}
                    p.help.settings-panel__hint {{ $t('A mailbox can pick a default in its connection settings.') }}

                    .table-top
                        el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="openSignature(null)")
                            | {{ $t('New signature') }}

                    el-table(ref="signatureTable" :data="signatures" @row-click="openSignature")
                        el-table-column(:label="$t('Name')" prop="name")
                        el-table-column(width="80" align="right")
                            template(slot-scope="{ row }")
                                el-button.delete-button(
                                    plain circle size="mini" type="danger"
                                    icon="el-icon-fa-trash-alt far"
                                    @click.stop="$refs.deleteSignatureDialog.open(row)"
                                )
                    confirm-delete-dialog(ref="deleteSignatureDialog" :title="$t('Delete signature')" :message="$t('The signature will be removed. Messages that already used it stay unchanged.')" @confirm="removeSignature")

            .section-intro.autoreplies-intro.is-collapsible(@click="showAutoReplies = !showAutoReplies")
                h4
                    i.section-intro__chevron(:class="showAutoReplies ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
                    |  {{ $t('Auto-replies') }}
                p.help {{ $t('Automatically acknowledge a customer message. Free-form inside the WhatsApp 24h window; email is always free.') }}

            .autoreply-channels(v-show="showAutoReplies" v-loading="autoRepliesLoading")
                //- Stacked, not side by side: the WhatsApp channel is ~3x the
                //- height of the email one, and a column with a 700px hole in it
                //- is not structure.
                .autoreply-channel.settings-panel(v-for="config in autoReplies" :key="config.channel")
                    .settings-panel__head
                        span.autoreply-channel__name
                            i(:class="config.channel === 'whatsapp' ? 'el-icon-fa-whatsapp fab' : 'el-icon-fa-envelope fas'")
                            |  {{ config.channel === 'whatsapp' ? 'WhatsApp' : $t('Email') }}
                        el-switch(v-model="config.enabled" :disabled="!canUpdateAccounts" :active-text="$t('Active')")
                    el-form.autoreply-channel__form.settings-panel__body(v-if="config.enabled" :disabled="!canUpdateAccounts" label-position="top")
                        //- Email: a plain message. WhatsApp: the message sits inside the shared
                        //- interactive builder's slot so the form follows the real card order
                        //- (header, message, footer, buttons).
                        el-form-item(v-if="config.channel === 'email'" :label="$t('Subject')")
                            el-input(v-model="config.subject" :placeholder="$t('Leave blank to keep the customer subject with a Re: prefix')" maxlength="150")
                        el-form-item(v-if="config.channel === 'email'" :label="$t('Message')")
                            quill-editor.autoreply-editor(v-model="config.message" :disabled="!canUpdateAccounts" :options="emailQuillConfig")
                        whatsapp-interactive-builder(
                            v-else
                            :value="config.interactive"
                            free-form
                            @input="value => config.interactive = value"
                        )
                            el-form-item(:label="$t('Message')")
                                //- WhatsApp-only formats; converted to WhatsApp markers server-side.
                                quill-editor.autoreply-editor(v-model="config.message" :disabled="!canUpdateAccounts" :options="whatsappQuillConfig")
                        //- Live card preview — the SAME shared renderer as the composer, templates
                        //- and order notifications, so the auto-reply shows exactly what the customer gets.
                        whats-app-card-preview(
                            v-if="config.channel === 'whatsapp' && autoReplyHasPreview(config)"
                            framed
                            :header-image="autoReplyCard(config).headerImage"
                            :header="autoReplyCard(config).header"
                            :body="autoReplyCard(config).body"
                            :footer="autoReplyCard(config).footer"
                            :buttons="autoReplyCard(config).buttons"
                        )
                        el-form-item(:label="$t('Send again at most every (minutes)')")
                            el-input-number(v-model="config.cooldown_minutes" :min="0" :step="30" size="mini")

            //- Collapsed by default: this block alone is longer than the rest of
            //- the page put together, and it is configured once and then left
            //- alone. v-show, NOT v-if — saveTabSettings() flushes it through
            //- $refs, and an unmounted ref would drop the save without a word.
            .section-intro.order-notifications-intro.is-collapsible(@click="showOrderNotifications = !showOrderNotifications")
                h4
                    i.section-intro__chevron(:class="showOrderNotifications ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
                    |  {{ $t('Order notifications via WhatsApp') }}
                p.help {{ $t('Deliver order events (confirmation, shipping, ...) as WhatsApp templates to customers who opted in. The email always remains the guaranteed channel.') }}

            communication-order-notifications(
                v-show="showOrderNotifications"
                ref="orderNotifications"
                :cms-id="activeCms.id"
                :disabled="!canUpdateAccounts"
                @manage-templates="$refs.connections.openWhatsAppConnection($event)"
            )

        //- Only where the ticket system is actually sold: a setting for a
        //- feature the shop does not have is noise it cannot act on.
        template(v-if="communicationEnabled && ticketsEnabled")
            .section-intro.ticket-template-intro.is-collapsible(@click="showTicketTemplate = !showTicketTemplate")
                h4
                    i.section-intro__chevron(:class="showTicketTemplate ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
                    |  {{ $t('Tickets via WhatsApp') }}
                p.help {{ $t('A ticket outlives the 24h WhatsApp window, and outside it Meta accepts approved templates only. This one opens a support conversation, and reopens it later.') }}

            //- The order notification event card, verbatim: head with the name, a
            //- rule divider, then the template row, its help line, the preview and
            //- the autofill hint. Only the content differs.
            .ticket-notifications(v-show="showTicketTemplate")
                el-alert(
                    v-if="!whatsappAccountId"
                    type="info"
                    :closable="false"
                    show-icon
                    :title="$t('Connect a WhatsApp number to reach requesters via WhatsApp.')"
                )
                .ticket-notification(v-else)
                    .ticket-notification__head
                        span.ticket-notification__name {{ $t('Support conversation') }}
                    el-form.ticket-notification__form(label-position="top" @submit.native.prevent)
                        el-form-item(:label="$t('Template')")
                            .ticket-notification__template
                                el-select.ticket-notification__template-select(
                                    v-model="ticketTemplateName"
                                    clearable
                                    filterable
                                    :loading="ticketTemplatesLoading"
                                    :placeholder="$t('Choose an approved template')"
                                    :disabled="!canUpdateAccounts"
                                    @change="saveTicketTemplate"
                                )
                                    el-option(
                                        v-for="template in ticketTemplateOptions"
                                        :key="template.name"
                                        :value="template.name"
                                        :label="template.name"
                                    )
                                el-button.ticket-notification__create(v-if="canUpdateMessages" type="text" icon="el-icon-fa-plus fas" @click="createSupportTemplate")
                                    | {{ $t('Create template') }}
                            //- Why the list is empty, and the way out of it — an empty select
                            //- with no explanation teaches nobody anything.
                            span.help.is-warning(v-if="ticketTemplatesUnavailable") {{ $t('The templates could not be loaded from Meta. Reconnect the WhatsApp number.') }}
                            span.help(v-else-if="!ticketTemplateOptions.length && !ticketTemplatesLoading") {{ $t('No approved template can open a support conversation yet. Create one to get started.') }}
                            span.help(v-else) {{ $t('Only approved utility templates can open a support conversation.') }}
                        //- Sibling of the form-item, not inside it: the item's own bottom
                        //- margin is what sets the preview off from the help line.
                        template(v-if="designatedTicketTemplate")
                            whats-app-card-preview.ticket-notification__preview(framed v-bind="ticketTemplateCard")
                            .ticket-notification__autofill(v-if="ticketTemplateAutoFilled.length")
                                i.el-icon-fa-magic.fas
                                span  {{ $t('Filled automatically from the ticket:') }} {{ ticketTemplateAutoFilled.join(' · ') }}

        full-height-drawer(
            :visible.sync="signatureDrawer.visible"
            size="560px"
            :title="signatureDrawer.form.id ? $t('Edit signature') : $t('New signature')"
        )
            el-form(label-position="top")
                el-form-item(:label="$t('Name')")
                    el-input(v-model="signatureDrawer.form.name")
                el-form-item(:label="$t('Signature')")
                    quill-editor.signature-editor(v-model="signatureDrawer.form.body" :options="signatureQuillConfig")
            template(#footer-left)
                el-button(round size="mini" icon="el-icon-fa-times fas" @click="signatureDrawer.visible = false") {{ $t('Cancel') }}
            template(#footer)
                //- "Ok", not "Save": this only puts the signature into the list.
                //- The page's Save is what writes it, together with everything
                //- else on the screen.
                el-button(
                    round size="mini" type="success"
                    icon="el-icon-fa-check fas"
                    :disabled="!String(signatureDrawer.form.name || '').trim()"
                    @click="applySignature"
                ) {{ $t('Ok') }}
</template>

<i18n>
{
    "de": {
        "The templates could not be loaded from Meta. Reconnect the WhatsApp number.": "Die Vorlagen konnten nicht von Meta geladen werden. Verbinde die WhatsApp-Nummer neu.",
        "Connections": "Verbindungen",
        "Email + WhatsApp / messenger channels. Click a connection to edit it.": "E-Mail- + WhatsApp-/Messenger-Kanäle. Klicke eine Verbindung an, um sie zu bearbeiten.",
        "Email sender configuration. Click a connection to edit it.": "E-Mail-Absender-Konfiguration. Klicke eine Verbindung an, um sie zu bearbeiten.",
        "Reusable signatures. A mailbox can pick a default in its connection settings.": "Wiederverwendbare Signaturen. Ein Postfach kann in seinen Verbindungseinstellungen eine Standard-Signatur wählen.",
        "Name": "Name",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "Something went wrong!": "Etwas ist schiefgelaufen!",
        "Mailbox saved": "Postfach gespeichert",
        "The mailbox could not be saved.": "Das Postfach konnte nicht gespeichert werden.",
        "Newsletter sender saved": "Newsletter-Absender gespeichert",
        "The newsletter sender could not be saved.": "Der Newsletter-Absender konnte nicht gespeichert werden.",
        "Tickets via WhatsApp": "Tickets über WhatsApp",
        "A ticket outlives the 24h WhatsApp window, and outside it Meta accepts approved templates only. This one opens a support conversation, and reopens it later.": "Ein Ticket lebt länger als das 24-Stunden-Fenster von WhatsApp, und außerhalb davon akzeptiert Meta ausschließlich freigegebene Vorlagen. Diese eröffnet eine Support-Konversation und öffnet sie wieder.",
        "Support conversation": "Support-Konversation",
        "Template": "Vorlage",
        "Filled automatically from the ticket:": "Wird automatisch aus dem Ticket befüllt:",
        "Choose an approved template": "Freigegebene Vorlage auswählen",
        "Create template": "Vorlage erstellen",
        "No approved template can open a support conversation yet. Create one to get started.": "Es gibt noch keine freigegebene Vorlage, die eine Support-Konversation eröffnen kann. Erstelle eine, um zu starten.",
        "Only approved utility templates can open a support conversation.": "Nur freigegebene Utility-Vorlagen können eine Support-Konversation eröffnen.",
        "Connect a WhatsApp number to reach requesters via WhatsApp.": "Verbinde eine WhatsApp-Nummer, um Anfragende per WhatsApp zu erreichen.",
        "Email signatures": "E-Mail-Signaturen",
        "Reusable text": "Wiederverwendbare Texte",
        "Wording your team inserts instead of typing it again: a sign-off at the end of a message, a prepared reply as its body.": "Formulierungen, die Ihr Team einfügt, statt sie erneut zu tippen: eine Grußformel am Ende einer Nachricht, eine vorbereitete Antwort als deren Text.",
        "A mailbox can pick a default in its connection settings.": "Ein Postfach kann in seinen Verbindungseinstellungen eine Standardsignatur festlegen.",
        "Reusable signatures. A mailbox can pick a default under “APIs & external code”.": "Wiederverwendbare Signaturen. Ein Postfach kann eine Standard-Signatur unter „APIs und Fremdcode“ wählen.",
        "New signature": "Neue Signatur",
        "Edit signature": "Signatur bearbeiten",
        "Ok": "Ok",
        "Signature": "Signatur",
        "Delete signature": "Signatur löschen",
        "The signature will be removed. Messages that already used it stay unchanged.": "Die Signatur wird entfernt. Bereits damit gesendete Nachrichten bleiben unverändert.",
        "Auto-replies": "Automatische Antworten",
        "Order notifications via WhatsApp": "Bestellbenachrichtigungen per WhatsApp",
        "Deliver order events (confirmation, shipping, ...) as WhatsApp templates to customers who opted in. The email always remains the guaranteed channel.": "Sende Bestellereignisse (Bestätigung, Versand, ...) als WhatsApp-Vorlagen an Kunden mit Einwilligung. Die E-Mail bleibt immer der garantierte Kanal.",
        "Automatically acknowledge a customer message. Free-form inside the WhatsApp 24h window; email is always free.": "Bestätigen Sie eine Kundennachricht automatisch. Frei formulierbar innerhalb des WhatsApp-24-Stunden-Fensters; E-Mail ist immer frei.",
        "Email": "E-Mail",
        "Active": "Aktiv",
        "Message": "Nachricht",
        "Subject": "Betreff",
        "Leave blank to keep the customer subject with a Re: prefix": "Leer lassen, um den Betreff des Kunden mit „Re:“ zu übernehmen",
        "Thanks for your message, we will get back to you shortly.": "Vielen Dank für Ihre Nachricht, wir melden uns in Kürze bei Ihnen.",
        "Send again at most every (minutes)": "Frühestens erneut senden nach (Minuten)"
    }
}
</i18n>

<script>
import _ from 'lodash'
import ConversationApi from '@/extensions/communication/store/api/conversation'
import { whatsappPlaceholderByKey } from '@/extensions/communication/utils/whatsappPlaceholders'
import { templateCardPreview } from '@/extensions/communication/utils/whatsappTemplateCard'
import { useSetupStore, useSettingsStore, useCommunicationStore } from '@/stores'
import { checkRestrictions } from '@/helpers'

import Error from '@/components/Error'
import FullHeightDrawer from '@/components/FullHeightDrawer'
import CommunicationConnections from '@/extensions/communication/views/ChannelAccount/Connections'
import WhatsAppInteractiveBuilder from '@/extensions/communication/views/Partials/WhatsAppInteractiveBuilder'
import WhatsAppCardPreview from '@/extensions/communication/views/Partials/WhatsAppCardPreview'
import { headerPreviewUrl } from '@/extensions/communication/utils/headerImageInput'
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog'
import CmsApi from '@/store/api/cms/cms'
import emptyMailbox from '@/extensions/communication/utils/emptyMailbox'
import MessagePresetManager from '@/extensions/communication/views/MessagePreset/Manager'
import AutoReplyApi from '@/extensions/communication/store/api/autoReply'
import CommunicationOrderNotifications from '@/extensions/communication/views/ChannelAccount/OrderNotifications'
import signatureQuillConfig from '@/quill/signatureConfig'
import composerConfig from '@/quill/composerConfig'
import whatsappConfig from '@/quill/whatsappConfig'

export default {
    name: 'CommunicationChannelAccounts',
    components: {
        Error,
        FullHeightDrawer,
        CommunicationConnections,
        // Explicit kebab key so the <whatsapp-interactive-builder> tag resolves (Vue would
        // otherwise auto-kebab the name to "whats-app-interactive-builder").
        'whatsapp-interactive-builder': WhatsAppInteractiveBuilder,
        'whats-app-card-preview': WhatsAppCardPreview,
        'communication-order-notifications': CommunicationOrderNotifications,
        ConfirmDeleteDialog,
        'message-preset-manager': MessagePresetManager
    },
    data() {
        return {
            signatureQuillConfig,
            emailQuillConfig: composerConfig,
            whatsappQuillConfig: whatsappConfig,
            // Collapsed on arrival: both are long, rarely-touched WhatsApp forms,
            // and open they are most of the page's 6000px.
            // Every section starts closed, so the page opens as a list of what
            // it contains rather than 3000px of forms.
            showReusableText: false,
            showAutoReplies: false,
            showOrderNotifications: false,
            showTicketTemplate: false,
            ticketTemplateName: null,
            ticketTemplateOptions: [],
            ticketTemplatesLoading: false,
            ticketTemplatesUnavailable: false,
            signatureDrawer: {
                visible: false,
                form: { id: null, name: '', body: '' }
            },
            autoReplies: [],
            autoRepliesLoading: false
        }
    },
    // ONE watch block. There were three keys in this options object, and a
    // duplicate key keeps only the last — `activeCms` had been dead since
    // `whatsappAccountId` was added below it, so switching shop stopped
    // reloading the auto-replies without anyone noticing.
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) {
                this.fetchAutoReplies()
            }
        },
        // The accounts arrive asynchronously, so the template list is loaded when
        // the WhatsApp connection actually appears rather than on mount.
        whatsappAccountId: {
            immediate: true,
            handler: 'loadTicketTemplates'
        },
        // Both tables live inside this section's v-show, so they measure their
        // columns while hidden (i.e. as zero) and keep that until told otherwise.
        showReusableText(open) {
            if (!open) return

            // A frame after $nextTick: the rows exist by then, but the grid has
            // not laid out, so a table measured now keeps the collapsed width.
            // BOTH tables go in the same frame — calling the child's relayout()
            // synchronously here schedules it a tick earlier, which is before the
            // grid has sized its columns, and it measures the old width.
            this.$nextTick(() => {
                window.requestAnimationFrame(() => {
                    if (this.$refs.signatureTable) this.$refs.signatureTable.doLayout()
                    if (this.$refs.messagePresets) this.$refs.messagePresets.relayout()
                })
            })
        },
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        // Auto replies, order notification rules and the ticket template are
        // saved through ex:comm:accounts update; writing a template is
        // ex:comm:messages update.
        canUpdateAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'update')
        },
        canUpdateMessages() {
            return checkRestrictions(['ex:comm:messages'], 'update')
        },
        // The settings form owns the list; this component only reads and edits
        // it, and the page's Save writes it back with the rest of the CMS.
        signatures() {
            return _.get(this.settingsForm, 'signatures') || []
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions || []
        },
        // The communication feature drives the WhatsApp rows, the email mailbox's
        // inbound/signature sections, and the signatures manager. Without it this
        // page is just the transactional + newsletter SMTP sender config.
        // The ticket system is a separately sold feature: its setting only exists
        // where the shop actually has it.
        ticketsEnabled() {
            return this.enabledExtensions.includes('tickets')
        },
        communicationStore() {
            return useCommunicationStore()
        },
        placeholderByKey() {
            const enabledExtensions = this.enabledExtensions
            return whatsappPlaceholderByKey({
                t: this.$t.bind(this),
                hasShop: enabledExtensions.includes('shop'),
                hasMembers: enabledExtensions.includes('shop') || enabledExtensions.includes('restricted-area'),
                hasTickets: enabledExtensions.includes('tickets')
            })
        },
        designatedTicketTemplate() {
            return this.ticketTemplateOptions.find(template => template.name === this.ticketTemplateName) || null
        },
        ticketTemplateCard() {
            return templateCardPreview(this.designatedTicketTemplate, this.placeholderByKey)
        },
        // What the desk fills in on its own, so the tenant knows the template needs
        // no manual work: the requester's name and the ticket's own number.
        ticketTemplateAutoFilled() {
            const keys = _.get(this.designatedTicketTemplate, 'variable_keys') || []
            return keys
                .map(key => _.get(this.placeholderByKey, [key, 'label']))
                .filter(Boolean)
        },
        // The account the support template belongs to — the CMS's own WhatsApp
        // connection (a POS keeps its own and is out of scope here).
        whatsappAccount() {
            return (this.communicationStore.accounts || [])
                .find(account => account.is_active && account.channel === 'whatsapp' && !_.get(account, 'point_of_sale.id'))
        },
        whatsappAccountId() {
            return _.get(this.whatsappAccount, 'id') || null
        },
        communicationEnabled() {
            return this.enabledExtensions.includes('communication')
                && checkRestrictions(['ex:comm', 'ex:comm:accounts'])
        },
        hasNewsletter() {
            return this.enabledExtensions.includes('newsletter')
        },
        // The transactional SMTP is needed whenever the template requires it
        // (shop / forms / …).
        needsSmtpCredentials() {
            const requirements = _.get(this.activeCms, 'config.template.requirements') || []
            return !!requirements.find(requirement => requirement.key === 'smtp-credentials' && requirement.context === 'cms')
        },
        // Reachable for SMTP (shop/forms), newsletter, or the communication feature.
        showPage() {
            return this.communicationEnabled || this.hasNewsletter || this.needsSmtpCredentials
        },
        // This is a Settings tab, so the email mailbox edits the same `form.smtp` the
        // page's Save persists (identical to “APIs & external code”).
        settingsForm() {
            return useSettingsStore().form
        },
        mailboxModel() {
            if (!this.settingsForm) return {}
            // Seed a fully-keyed object so SmtpCredentials' field computeds stay reactive
            // (a bare {} freezes every input; see emptyMailbox()).
            if (!this.settingsForm.smtp) this.$set(this.settingsForm, 'smtp', emptyMailbox())
            return this.settingsForm.smtp
        },
        newsletterModel() {
            // Only materialise form.newsletterSmtp when the newsletter row is shown,
            // so a non-newsletter CMS never gets a stray empty newsletter channel.
            if (!this.settingsForm || !this.hasNewsletter) return {}
            if (!this.settingsForm.newsletterSmtp) this.$set(this.settingsForm, 'newsletterSmtp', {})
            return this.settingsForm.newsletterSmtp
        }
    },
    mounted() {
        this.fetchAutoReplies()
        // Connections keeps its own copy for the rows; the designation needs the
        // WhatsApp account here, so the shared store is filled once.
        if (this.ticketsEnabled) this.communicationStore.fetchAccounts()
    },
    methods: {
        // A WhatsApp number was connected, changed or removed, or a template created: the
        // sections that read it once on mount load it again, without touching their unsaved edits.
        onWhatsAppChanged() {
            if (this.$refs.orderNotifications) {
                this.$refs.orderNotifications.loadWhatsApp()
                    .catch(() => this.notifyError(this.$t('Something went wrong!')))
            }
            if (this.ticketsEnabled) {
                this.communicationStore.fetchAccounts()
                this.loadTicketTemplates()
            }
        },
        loadTicketTemplates() {
            if (!this.whatsappAccountId) return

            this.ticketTemplateName = _.get(this.whatsappAccount, 'providerConfig.ticket_template_name') || null
            this.ticketTemplatesLoading = true
            this.ticketTemplatesUnavailable = false
            ConversationApi.fetchWhatsAppTemplates(this.whatsappAccountId)
                .then(templates => { this.ticketTemplateOptions = templates || [] })
                .catch(() => {
                    this.ticketTemplateOptions = []
                    this.ticketTemplatesUnavailable = true
                })
                .finally(() => { this.ticketTemplatesLoading = false })
        },
        // Designating is a single setting, so it saves on change rather than
        // making the agent hunt for a Save button for one select.
        saveTicketTemplate(name) {
            this.communicationStore.saveAccount({
                id: this.whatsappAccountId,
                // Meta's routing fields are protected server-side; only this key
                // is written.
                providerConfig: { ticket_template_name: name || null }
            })
                .then(() => this.communicationStore.fetchAccounts())
                .catch(() => this.notifyError(this.$t('Something went wrong!')))
        },
        // The recommended support template, opened prefilled in the manager: the
        // shape is not obvious (Utility category, ticket variables, and quick
        // replies that reopen the window), so it is shown rather than described.
        createSupportTemplate() {
            this.$refs.connections.openWhatsAppConnection({
                name: 'support_anfrage_rueckmeldung_v1',
                category: 'UTILITY',
                language: 'de',
                body: 'Hallo {{1}}, zu Ihrer Anfrage {{2}} haben wir eine Rückmeldung für Sie.',
                variable_keys: ['recipient.name', 'ticket.number'],
                footer: 'Tippen Sie eine Antwort an, um das Gespräch fortzusetzen.',
                buttons: [
                    { type: 'QUICK_REPLY', text: 'Antworten' },
                    { type: 'QUICK_REPLY', text: 'Erledigt, danke' },
                ],
            })
        },
        checkRestrictions,
        // The WhatsApp auto-reply as WhatsAppCardPreview props — the live message (body) plus the
        // interactive builder's header/footer/buttons, so it previews like every other card.
        autoReplyCard(config) {
            const model = config.interactive || {}
            const hasBody = (config.message || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
            return {
                headerImage: model.header_format === 'IMAGE'
                    ? (headerPreviewUrl(model.header_image) || model.header_media_url || '')
                    : '',
                header: model.header_format === 'TEXT' ? (model.header || '') : '',
                body: hasBody ? config.message : '',
                footer: (model.footer || '').trim(),
                buttons: (model.buttons || []).filter(button => (button.text || '').trim())
            }
        },
        autoReplyHasPreview(config) {
            const card = this.autoReplyCard(config)
            return !!(card.headerImage || card.header || card.body || card.footer || card.buttons.length)
        },
        fetchAutoReplies() {
            if (!this.activeCms || !this.communicationEnabled) return
            this.autoRepliesLoading = true
            AutoReplyApi.fetchList(this.activeCms.id)
                .then(list => { this.autoReplies = this.mergeAutoReplyChannels(list || []) })
                .catch(() => this.notifyError(this.$t('Something went wrong!')))
                .finally(() => { this.autoRepliesLoading = false })
        },
        // Always surface both channels, seeded from any saved configuration.
        mergeAutoReplyChannels(list) {
            return ['email', 'whatsapp'].map(channel => {
                const existing = _.find(list, { channel })
                return {
                    channel,
                    enabled: existing ? !!existing.enabled : false,
                    message: existing ? (existing.message || '') : '',
                    // Optional custom subject (email only); blank replies with "Re: <thread subject>".
                    subject: existing ? (existing.subject || '') : '',
                    cooldown_minutes: existing ? (existing.cooldown_minutes || 240) : 240,
                    // The interactive chrome (WhatsApp only) — header/footer/buttons authored via
                    // the shared builder, seeded from the saved config.
                    interactive: {
                        header_format: _.get(existing, 'header_format') || 'NONE',
                        header: _.get(existing, 'header') || '',
                        // Rehydrate the saved header Image (source + crop + variants) so the shared
                        // builder's editor can RE-CROP it; header_media_url is the baked preview url.
                        header_image: _.get(existing, 'headerImage') || null,
                        header_media_url: _.get(existing, 'header_media_url') || '',
                        footer: _.get(existing, 'footer') || '',
                        buttons: _.get(existing, 'buttons') || []
                    }
                }
            })
        },
        // Flushed by the settings shell's Save (parent form), via the same hook the
        // billing tab uses — no per-section save button.
        async saveTabSettings() {
            if (!this.activeCms || !this.communicationEnabled || !this.canUpdateAccounts) return
            if (this.autoReplies.length) {
                await Promise.all(this.autoReplies.map(config => AutoReplyApi.save(this.activeCms.id, config)))
            }
            if (this.$refs.orderNotifications) {
                await this.$refs.orderNotifications.save()
            }
        },
        // Save/Ok: on this settings tab the CMS is always persisted, so the connection
        // drawers "Save" the mailbox now — a PARTIAL CMS save of only `smtp` (or the
        // deletion flag), never the whole settings form (which may hold unsaved edits in
        // other tabs). Replace the model with the response so the row reflects saved state.
        persistCmsMailbox() {
            return CmsApi.update({ id: this.settingsForm.id, smtp: this.mailboxModel })
                .then(cms => {
                    this.$set(this.settingsForm, 'smtp', _.get(cms, 'smtp') || emptyMailbox())
                    this.notifySuccess(this.$t('Mailbox saved'))
                })
                .catch(() => this.notifyError(this.$t('The mailbox could not be saved.')))
        },
        persistCmsNewsletter() {
            return CmsApi.update({ id: this.settingsForm.id, newsletterSmtp: this.newsletterModel })
                .then(cms => {
                    this.$set(this.settingsForm, 'newsletterSmtp', _.get(cms, 'newsletterSmtp') || {})
                    this.notifySuccess(this.$t('Newsletter sender saved'))
                })
                .catch(() => this.notifyError(this.$t('The newsletter sender could not be saved.')))
        },
        openSignature(signature) {
            this.signatureDrawer.form = signature
                ? { id: signature.id, name: signature.name, body: signature.body || '' }
                : { id: null, name: '', body: '' }
            this.signatureDrawer.visible = true
        },
        // Ok: into the settings form, not into the database. The page's Save
        // sends the whole list with the CMS, and what is missing from it is
        // deleted server-side.
        applySignature() {
            const form = { ...this.signatureDrawer.form }
            const signatures = [...this.signatures]
            const index = signatures.findIndex(signature => signature.id === form.id)

            if (index === -1) {
                // A placeholder id so the table can key on the row before the
                // server has given it a real one. Negative, so it can never
                // collide with one — and the API drops it, which is what makes
                // the row a create.
                form.id = Math.min(0, ...signatures.map(signature => Number(signature.id) || 0)) - 1
                signatures.push(form)
            } else {
                signatures.splice(index, 1, form)
            }

            this.$set(this.settingsForm, 'signatures', signatures)
            this.signatureDrawer.visible = false
            this.$nextTick(() => {
                if (this.$refs.signatureTable) this.$refs.signatureTable.doLayout()
            })
        },
        removeSignature(signature) {
            this.$set(
                this.settingsForm,
                'signatures',
                this.signatures.filter(candidate => candidate.id !== signature.id)
            )
        },
        notifySuccess(title) {
            this.$notify.success({
                title,
                iconClass: 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        },
        notifyError(title) {
            this.$notify.error({
                title,
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        }
    }
}
</script>

<style lang="sass" scoped>
.section-intro
    margin-bottom: 1rem
    h4
        margin: 0 0 4px
        color: $color-grey-text
    .help
        line-height: 1.4
        margin: 0
.table-top
    display: flex
    gap: 10px
    margin-bottom: 15px
.reusable-text-intro
    margin-top: 2.5rem
.signature-editor
    ::v-deep .ql-editor
        min-height: 160px
        font-size: 13px
.autoreplies-intro
    margin-top: 2.5rem
// This one carried the modifier class in the template but never had a rule, so
// it sat flush against the auto-replies above and read as part of them.
.order-notifications-intro
    margin-top: 2.5rem
.ticket-template-intro
    margin-top: 2.5rem

// A whole section that opens and closes: the heading IS the control, so the
// page keeps its flat h4 + hint language instead of growing a bordered widget.
.is-collapsible
    cursor: pointer
    user-select: none

    h4:hover
        color: $--color-primary

    .section-intro__chevron
        display: inline-block
        width: 12px
        font-size: 11px
        color: #909399

// Peers of the same kind sit beside each other, but only once a column is wide
// enough to read: below this they stack, which is also every phone and half the
// laptops. align-items: start so the shorter panel ends where its content does
// instead of stretching to match its neighbour.
.settings-columns
    display: grid
    gap: 16px
    align-items: start

    // 72em is the project's `larger` step ($min-width in _bp.sass). Written out
    // rather than +bp(larger) because _init.sass prepends only _variables and
    // _mixins — the bp() mixin is not reachable from a component's styles.
    @media (min-width: 72em)
        grid-template-columns: 1fr 1fr

    // The panel's own hint spacing; the table follows it directly.
    .settings-panel__hint
        margin: 4px 0 12px
// Copied from OrderNotifications' event card so the two template settings are the
// same element: card, head + rule, template row, preview, autofill hint.
.ticket-notifications
    .help
        display: block
        line-height: 1.4
        font-size: .75rem
        color: #909399
        margin-top: 4px
    .ticket-notification
        padding: 14px 16px
        background: #fff
        border: 1px solid #ebeef5
        border-radius: 8px
        .ticket-notification__head
            display: flex
            align-items: center
            justify-content: space-between
            gap: 8px
        .ticket-notification__name
            font-weight: 600
        .ticket-notification__form
            margin-top: 14px
            padding-top: 14px
            border-top: 1px solid #f2f4f7
            // Give the stacked form-items real breathing room (Element's default is cramped here).
            ::v-deep .el-form-item
                margin-bottom: 18px
                &:last-child
                    margin-bottom: 0
        .ticket-notification__template
            display: flex
            align-items: center
            gap: 10px
            .ticket-notification__template-select
                flex: 1
                min-width: 0
            .ticket-notification__create
                flex-shrink: 0
                padding: 0
        .ticket-notification__preview
            margin: 4px 0 10px
        .ticket-notification__autofill
            display: flex
            align-items: baseline
            gap: 5px
            font-size: .78rem
            line-height: 1.5
            color: #909399
            i
                color: #409EFF
.autoreply-channels
    display: flex
    flex-direction: column
    gap: 12px
// A channel is one configurable thing, so it gets the same panel every other
// sub-block on this page has (.settings-panel, globals.sass). It used to be a
// flat row with a hairline above, which read as a continuation of the row before
// it rather than a separate setting.
.autoreply-channel
    // No colour of its own: a panel name inherits the body colour like every
    // other one on this page (.settings-panel__name, .notification-event__name,
    // .ticket-notification__name). It was grey while these were flat rows
    // sharing the section's chrome; as a panel head that made it the only
    // heading of its kind in a different shade.
    &__name
        font-weight: 600
        i
            margin-right: 6px
.autoreply-editor
    ::v-deep .ql-editor
        min-height: 90px
        font-size: 13px
</style>
